import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getWellness, getRPE, getGPS, getPlayers, getScenarios, getLoadScenarios, getPendingForms, getScheduleDay, resolveTargetsBatch } from '../../api';
import { SCENARIO_DAYS, parseDate } from '../../scenarioDays';

// UTC-safe date-add — see FutureTargets.js's identical fix (2026-08-30): `new Date(str)` +
// setDate() + toISOString() parses/mutates in LOCAL time then reads back the UTC date, which
// rolls the result back a day for anyone in a UTC+ timezone (Europe/Amsterdam) near midnight.
// Needed here for weeklyHMLDTarget's day-by-day date list.
function addDaysUTC(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

// LOCAL calendar date, not UTC — toISOString() converts to UTC first, which rolls "today"
// back to "yesterday" for any positive UTC offset (e.g. Netherlands/CEST) during the local
// midnight–2am window, when UTC hasn't crossed into the new date yet.
const tod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Player's own gym assignment for a schedule_day row, from the coach's per-player Gym
// breakdown popup (Week Schedule, added 2026-08-20). A day nobody has been individually
// ticked for yet (gym_player_focus empty/all-false) falls back to the whole-team Gym focus
// body diagram (body_parts, the front/back silhouette) instead — everyone's assigned
// whenever AM or PM is 'gym', with the accent read off whichever zones the coach tagged
// there (front vs back is a diagram-only distinction, collapsed away here since the player
// app only shows upper/lower/other). Once the coach ticks at least one box in the per-player
// popup, that takes over completely and an untouched player reads as "no gym" instead of
// inheriting the team-wide default.
export function gymFocusFor(sched, pid) {
  const focusMap = sched?.gym_player_focus || {};
  const anyAssigned = Object.values(focusMap).some(v => v && (v.upper || v.lower || v.other));
  const teamGymPlanned = sched?.session_am_type === 'gym' || sched?.session_pm_type === 'gym';
  if (!anyAssigned) {
    if (!teamGymPlanned) return { assigned: false, upper: false, lower: false, other: false, teamWide: true };
    const zones = [...(sched?.body_parts?.front || []), ...(sched?.body_parts?.back || [])];
    return { assigned: true, upper: zones.includes('upper_body'), lower: zones.includes('lower_body'), other: false, teamWide: true };
  }
  const mine = focusMap[pid] || {};
  return { assigned: !!(mine.upper || mine.lower || mine.other), upper: !!mine.upper, lower: !!mine.lower, other: !!mine.other, teamWide: false };
}

function isoWeek(dateStr) {
  const d = new Date(dateStr); d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const w1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - w1) / 86400000 - 3 + (w1.getDay() + 6) % 7) / 7);
}
function isoYear(dateStr) {
  const d = new Date(dateStr); d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  return d.getFullYear();
}

const Ctx = createContext(null);

export function PlayerDataProvider({ playerRecord, sessionToken, children }) {
  const [rawWellness, setRawWellness] = useState([]);
  const [rawRpe,      setRawRpe]      = useState([]);
  const [rawGps,      setRawGps]      = useState([]);
  const [rawPlayers,  setRawPlayers]  = useState([]);
  const [rawScenarios, setRawScenarios] = useState([]);
  const [rawLoadScenarios, setRawLoadScenarios] = useState([]);
  const [pendingForms, setPendingForms] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState(null);
  const [loading,     setLoading]     = useState(true);

  const fetchAll = () => {
    if (!playerRecord?.id) { setLoading(false); return Promise.resolve(); }
    return Promise.all([getWellness(), getRPE(), getGPS(), getPlayers(), getScenarios(), getLoadScenarios(), getPendingForms(playerRecord.id),
      // Never let a missing/unplanned schedule day break the whole player app — the RPE tiles
      // fall back to pitch-only when this is null (see rpeForms below).
      getScheduleDay(tod()).catch(() => null)])
      .then(([w, r, g, p, sc, ls, pf, sd]) => {
        setRawWellness(w || []);
        setRawRpe(r || []);
        setRawGps(g || []);
        setRawPlayers(p || []);
        setRawScenarios(sc || []);
        setRawLoadScenarios(ls || []);
        setPendingForms(pf || []);
        setTodaySchedule(sd || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, [playerRecord?.id]); // eslint-disable-line

  const refresh = () => fetchAll();

  const todayStr = tod();
  const pid   = playerRecord?.id   ? String(playerRecord.id)             : null;
  const pname = playerRecord?.name ? playerRecord.name.toLowerCase()     : '';

  const todayWellness = useMemo(() => {
    if (!pid) return null;
    // submitted_at NULL = a row the Oura sync created for sleep data; the player hasn't
    // filled in the questionnaire yet, so it doesn't count as today's submission.
    const e = rawWellness.find(e =>
      String(e.player_id) === pid && (e.date || '').slice(0, 10) === todayStr && e.submitted_at
    );
    if (!e) return null;
    return {
      sleepQuality:   e.sleep_quality,
      sleepHrv:       e.sleep_hrv,
      musclesoreness: e.muscle_soreness,
      stressLevel:    e.stress_level,
      sleepHours:     e.sleep_hours,
      otherPain:      e.other_pain,
    };
  }, [rawWellness, pid, todayStr]);

  // Pitch and gym RPE are independent submissions on the same date (2026-08-17) — each tile
  // tracks its own done/pending state. Rows written before the split have no session_type of
  // their own, but the column defaults to 'pitch' server-side, so they read as pitch here too.
  const todayRpeAll = useMemo(() => {
    if (!pid) return [];
    return rawRpe.filter(r =>
      String(r.player_id) === pid && (r.date || '').slice(0, 10) === todayStr
    );
  }, [rawRpe, pid, todayStr]);
  const todayRpePitch = useMemo(() => todayRpeAll.find(r => (r.session_type || 'pitch') === 'pitch') || null, [todayRpeAll]);
  const todayRpeGym   = useMemo(() => todayRpeAll.find(r => r.session_type === 'gym') || null, [todayRpeAll]);
  // Kept for existing consumers that only care whether *any* RPE landed today.
  const todayRpe = todayRpePitch || todayRpeGym;

  // Which RPE forms today actually calls for, from the week schedule's AM/PM session types
  // (2026-08-17) plus the coach's per-player Gym breakdown (2026-08-20, see gymFocusFor) —
  // 'individual' sessions have no per-player breakdown of their own so they stay unconditional,
  // same as before. Pitch stays unconditional: it's the long-standing default, and hiding it on
  // an unplanned/unscheduled day would silently stop collecting the metric the whole load model
  // is built on. A submission that already exists always keeps its form visible, so a player can
  // still see what they entered even if the plan changed after.
  const todayGymFocus = useMemo(() => gymFocusFor(todaySchedule, pid), [todaySchedule, pid]);
  const rpeForms = useMemo(() => {
    const types = [todaySchedule?.session_am_type, todaySchedule?.session_pm_type].filter(Boolean);
    const individualPlanned = types.includes('individual');
    return { pitch: true, gym: todayGymFocus.assigned || individualPlanned || !!todayRpeGym };
  }, [todaySchedule, todayGymFocus, todayRpeGym]);

  // Session list without targets — the raw material for both the resolve effect below and the
  // final mySessions memo, kept separate so the batch call has a stable dependency that doesn't
  // change shape every time resolvedBySession itself updates.
  const mySessionsBase = useMemo(() => {
    if (!pid) return [];
    const db = rawPlayers.find(p => String(p.id) === pid);
    const pos = db?.position || playerRecord?.position || null;
    return rawGps.flatMap(sess => {
      const entry = sess.players?.find(p =>
        String(p.player_id) === pid ||
        (p.player_name || '').toLowerCase() === pname
      );
      if (!entry) return [];
      return [{
        sessionId:      sess.id,
        date:           (sess.session_date || '').slice(0, 10),
        title:          sess.session_title || '',
        scenario: sess.scenario || '', mdLabel: entry.md_label || entry.mdLabel || '', position: pos,
        td:             parseFloat(entry.td)             || 0,
        hid:            parseFloat(entry.hid)            || 0,
        sd:             parseFloat(entry.sd)             || 0,
        sa:             parseFloat(entry.sa)             || 0,
        hmld:           parseFloat(entry.hmld)           || 0,
        expl:           parseFloat(entry.expl)           || 0,
        max_speed:      parseFloat(entry.max_speed)      || 0,
        minutes_played: parseFloat(entry.minutes_played) || 0,
      }];
    }).sort((a, b) => b.date.localeCompare(a.date));
  }, [rawGps, pid, pname, rawPlayers, playerRecord?.position]);

  // Individualized target resolution — batched through /api/targets/resolve-batch
  // (targetResolution.js, backend). Previously this had NO player_id filter at all (matched by
  // scenario name only, so a teammate's individual override could apply to the wrong player) and
  // NO intensity/MD scaling whatsoever. Deliberately does not clear resolvedBySession before a
  // new batch resolves — this context feeds many player-app screens, so a still-in-flight
  // refetch should keep showing the last-known targets, not flash every session to empty.
  const [resolvedBySession, setResolvedBySession] = useState(new Map());
  useEffect(() => {
    if (!pid || !mySessionsBase.length) { setResolvedBySession(new Map()); return; }
    const items = mySessionsBase.map(s => ({
      key: String(s.sessionId), playerId: pid, position: s.position, scenario: s.scenario, date: s.date, dayLabel: s.mdLabel,
    }));
    let cancelled = false;
    resolveTargetsBatch(items)
      .then(({ results }) => { if (!cancelled) setResolvedBySession(new Map(results.map(r => [r.key, r]))); })
      .catch(err => console.error('Failed to resolve player session targets:', err));
    return () => { cancelled = true; };
  }, [mySessionsBase, pid]);

  const mySessions = useMemo(() => {
    return mySessionsBase.map(s => ({ ...s, targets: resolvedBySession.get(String(s.sessionId))?.targets || null }));
  }, [mySessionsBase, resolvedBySession]);

  const lastSession = mySessions[0] || null;

  // The "week" a player actually trains in is whichever scenario's day-range is
  // active today (e.g. a 7-day pre-season microcycle starting mid-week), not the
  // Monday-Sunday ISO calendar week — those don't line up at season-boundary/
  // pre-season scenarios, which silently undercounted "this week"'s sessions.
  // Shared by thisWeek (actual) and weeklyHMLDTarget (target) so both are scoped
  // to the same window.
  const activeScenarioWindow = useMemo(() => {
    const db  = rawPlayers.find(p => String(p.id) === pid);
    const pos = db?.position || playerRecord?.position || null;
    if (!pos) return null;

    const customDaysMap = {};
    rawLoadScenarios.forEach(t => { customDaysMap[t.name] = t.days; });
    const daysMap = { ...SCENARIO_DAYS, ...customDaysMap };

    const candidates = rawScenarios.filter(sc => sc.player_id === 'team' || String(sc.player_id) === pid);
    const matching = candidates.filter(sc => {
      const days  = daysMap[sc.scenario];
      const start = parseDate(sc.start_date);
      if (!days || !start) return false;
      const idx = Math.round((parseDate(todayStr) - start) / 86400000);
      return idx >= 0 && idx < days.length;
    });
    if (!matching.length) return null;
    const assignment = matching.find(sc => String(sc.player_id) === pid) || matching.find(sc => sc.player_id === 'team') || matching[0];

    const days  = daysMap[assignment.scenario];
    const start = parseDate(assignment.start_date);
    const end   = new Date(start);
    end.setDate(end.getDate() + days.length - 1);

    return { assignment, days, pos, startStr: start.toISOString().slice(0, 10), endStr: end.toISOString().slice(0, 10) };
  }, [pid, rawPlayers, playerRecord?.position, rawScenarios, rawLoadScenarios, todayStr]);

  const thisWeek = useMemo(() => {
    if (activeScenarioWindow) {
      return mySessions.filter(s => s.date && s.date >= activeScenarioWindow.startStr && s.date <= activeScenarioWindow.endStr);
    }
    const nw = isoWeek(todayStr), ny = isoYear(todayStr);
    return mySessions.filter(s => s.date && isoWeek(s.date) === nw && isoYear(s.date) === ny);
  }, [mySessions, todayStr, activeScenarioWindow]);

  const weekNum = isoWeek(todayStr);

  const weeklyHMLD = thisWeek.reduce((s, x) => s + x.hmld, 0);
  const weeklyTD   = thisWeek.reduce((s, x) => s + x.td, 0);

  // Full-week HMLD target: sum every day defined in whichever scenario is active today (not
  // just the days the player has actually trained so far) — e.g. a 7-day microcycle's full HMLD
  // target, not the sum of the 2 sessions completed so far this week. Individualized target
  // resolution — batched through /api/targets/resolve-batch (targetResolution.js, backend).
  // Previously this applied `intensity` uniformly to EVERY day including MD (no MD/md_minutes
  // branch at all — MD should scale by minutes played, never by training intensity, same rule
  // every other target consumer in the app follows) and had no day_intensity support.
  const [resolvedWeekDays, setResolvedWeekDays] = useState(new Map());
  useEffect(() => {
    if (!activeScenarioWindow || !pid) { setResolvedWeekDays(new Map()); return; }
    const { assignment, days, pos } = activeScenarioWindow;
    const items = days.map((label, i) => {
      const dateStr = addDaysUTC(assignment.start_date.slice(0, 10), i);
      return { key: dateStr, playerId: pid, position: pos, scenario: assignment.scenario, date: dateStr, dayLabel: label };
    });
    let cancelled = false;
    resolveTargetsBatch(items)
      .then(({ results }) => { if (!cancelled) setResolvedWeekDays(new Map(results.map(r => [r.key, r]))); })
      .catch(err => console.error('Failed to resolve weekly HMLD target:', err));
    return () => { cancelled = true; };
  }, [activeScenarioWindow, pid]);

  const weeklyHMLDTarget = useMemo(() => {
    if (!activeScenarioWindow) return 0;
    let total = 0;
    resolvedWeekDays.forEach(r => {
      const lower = r.targets?.HMLD?.lower;
      if (lower != null) total += lower;
    });
    return total;
  }, [activeScenarioWindow, resolvedWeekDays]);

  const allTimeMaxSpd = mySessions.reduce((mx, s) => Math.max(mx, s.max_speed), 0);

  // playerRecord is a one-time snapshot taken at login; merge in the latest row
  // from rawPlayers (refreshed via refresh()) so edits made in Settings show up
  // immediately everywhere this context is consumed.
  const freshPlayerRecord = useMemo(() => {
    if (!playerRecord?.id) return playerRecord;
    const updated = rawPlayers.find(p => String(p.id) === String(playerRecord.id));
    return updated ? { ...playerRecord, ...updated } : playerRecord;
  }, [playerRecord, rawPlayers]);

  return (
    <Ctx.Provider value={{
      loading, rawWellness, rawRpe, rawGps, rawPlayers, rawScenarios, rawLoadScenarios,
      todayStr, pid, playerRecord: freshPlayerRecord, sessionToken,
      todayWellness, todayRpe, todayRpePitch, todayRpeGym, todaySchedule, rpeForms, todayGymFocus,
      mySessions, lastSession, thisWeek, weekNum,
      weeklyHMLD, weeklyHMLDTarget, weeklyTD, allTimeMaxSpd,
      pendingForms,
      refresh,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const usePlayerData = () => useContext(Ctx);
